import type { ReactNode } from "react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { usePageHeader } from "@/layout/usePageHeader";

export interface AppShellProps {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Interner Router-Link (zurück innerhalb der SPA) – ohne `/react`-Präfix. */
  back?: { to: string; label: string };
  /** Externer Link in die klassische App (mit vollständigem Pfad). */
  backExternal?: { href: string; label: string };
  children: ReactNode;
  className?: string;
}

/**
 * Inhalt einer Seite. Die Seitenüberschrift steht im persistierenden Header
 * (über der Navigation); hier bleiben nur Zurück-Link, Aktionsbereich und Inhalt.
 */
export function AppShell({
  title,
  subtitle,
  actions,
  back,
  backExternal,
  children,
  className,
}: AppShellProps) {
  const { setPageMeta } = usePageHeader();
  const subtitleStr = typeof subtitle === "string" ? subtitle : undefined;

  useEffect(() => {
    setPageMeta({ title, subtitle: subtitleStr });
    return () => setPageMeta(null);
  }, [title, subtitleStr, setPageMeta]);

  return (
    <div
      className={clsx("min-w-0 max-w-full space-y-6", className)}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          {(back || backExternal) && (
            <div className="text-sm">
              {back ? (
                <Link
                  to={back.to}
                  className="text-label-secondary hover:text-label"
                >
                  ← {back.label}
                </Link>
              ) : backExternal ? (
                <a
                  href={backExternal.href}
                  className="text-label-secondary hover:text-label"
                >
                  ← {backExternal.label}
                </a>
              ) : null}
            </div>
          )}
          {subtitle !== undefined && subtitle !== null && typeof subtitle !== "string" && (
            <div className="max-w-2xl text-sm text-label-secondary">{subtitle}</div>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div className="min-w-0 max-w-full space-y-6">{children}</div>
    </div>
  );
}
